<script lang="ts">
  import TimeInput from "./TimeInput.svelte";
  import { untrack } from "svelte";
  import type { Payment } from "@split/core";
  import Drawer from "./Drawer.svelte";
  import Swatch from "./Swatch.svelte";
  import { app } from "../lib/store.svelte.ts";
  import { centsInput, formatMicro, nowLocal, readMoney } from "../lib/format.ts";

  let { payment = null, personId = "", amountCents = 0, onclose }: { payment?: Payment | null; personId?: string; amountCents?: number; onclose: () => void } = $props();

  const S = $derived(app.current);
  const people = $derived(S.personOrder.map((id) => S.people[id]!).filter((p) => p && !p.archived && p.id !== S.settings.ownerPersonId));

  // The form starts from the values passed in when the panel opened.
  const init = untrack(() => payment);
  const startPerson = untrack(() => personId);
  const startAmount = untrack(() => amountCents);
  let who = $state(init?.personId ?? startPerson ?? "");
  let amount = $state(init ? centsInput(Math.abs(init.cents)) : startAmount > 0 ? centsInput(startAmount) : "");
  let direction = $state<"in" | "out">(init && init.cents < 0 ? "out" : "in");
  let note = $state(init?.note ?? "");
  let at = $state(init?.at ?? nowLocal());

  const cents = $derived(readMoney(amount));
  const valid = $derived(!!who && cents !== null && cents > 0 && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(at));
  const balance = $derived(who ? app.ledger.byPersonId[who] : undefined);
  const quick = ["Zelle", "Venmo", "Cash App", "Cash", "Apple Pay"];

  async function save() {
    if (!valid || cents === null) return;
    const signed = direction === "in" ? cents : -cents;
    const name = S.people[who]?.name ?? "";
    let err: string | null;
    if (!payment) {
      err = await app.do_(
        { type: "payment.add", paymentId: crypto.randomUUID(), at, personId: who, cents: signed, note: note.trim() },
        direction === "in" ? `Logged ${name}'s payment` : `Logged your payment to ${name}`,
      );
    } else {
      err = await app.do_({ type: "payment.update", paymentId: payment.id, patch: { at, personId: who, cents: signed, note: note.trim() } }, "Saved");
    }
    if (!err) onclose();
  }

  async function remove() {
    if (!payment) return;
    const copy = { ...payment };
    const err = await app.do_({ type: "payment.remove", paymentId: payment.id });
    if (err) return;
    onclose();
    app.toast("Removed the payment", "info", {
      label: "Undo",
      run: () => void app.do_({ type: "payment.add", paymentId: copy.id, at: copy.at, personId: copy.personId, cents: copy.cents, note: copy.note }),
    });
  }
</script>

<Drawer title={payment ? "Edit payment" : "Log a payment"} {onclose}>
  <form class="form" id="pay-form" onsubmit={(e) => { e.preventDefault(); void save(); }}>
    <div class="dir" role="radiogroup" aria-label="Direction">
      <button type="button" role="radio" aria-checked={direction === "in"} class:on={direction === "in"} onclick={() => (direction = "in")}>They paid you</button>
      <button type="button" role="radio" aria-checked={direction === "out"} class:on={direction === "out"} onclick={() => (direction = "out")}>You paid them</button>
    </div>

    <fieldset class="people">
      <legend>Who</legend>
      {#each people as p (p.id)}
        <label class="person" class:on={who === p.id}>
          <input type="radio" name="who" value={p.id} bind:group={who} />
          <Swatch color={p.color} status={who === p.id ? "paid" : "unpaid"} size={18} />
          {p.name}
        </label>
      {/each}
    </fieldset>

    {#if balance && balance.status === "owes" && direction === "in"}
      <p class="hint-line">
        {balance.name} owes {formatMicro(balance.balanceMicro)}.
        <button type="button" class="link" onclick={() => (amount = centsInput(Math.round(balance.balanceMicro / 10_000)))}>Fill that in</button>
      </p>
    {/if}

    <div class="row2">
      <label class="field">
        <span>Amount</span>
        <input class="input money" inputmode="decimal" bind:value={amount} placeholder="0.00" aria-invalid={amount !== "" && (cents === null || cents <= 0)} />
      </label>
      <div class="field">
        <span>When</span>
        <span class="when">
          <input class="input" type="date" aria-label="Date" value={at.slice(0, 10)} onchange={(e) => (at = `${(e.currentTarget as HTMLInputElement).value}T${at.slice(11, 16) || "12:00"}`)} />
          <TimeInput label="Time" bind:value={() => at.slice(11, 16), (v) => (at = `${at.slice(0, 10)}T${v}`)} />
        </span>
      </div>
    </div>

    <label class="field">
      <span>Note</span>
      <input class="input" bind:value={note} placeholder="How they paid, or anything else" />
    </label>
    <div class="quick">
      {#each quick as q}<button type="button" class="btn small" onclick={() => (note = q)}>{q}</button>{/each}
    </div>
  </form>

  {#snippet footer()}
    <span class="spacer"></span>
    {#if payment}<button class="btn quiet danger" onclick={remove}>Remove</button>{/if}
    <button class="btn primary" type="submit" form="pay-form" disabled={!valid || !app.canEdit}>{payment ? "Save changes" : "Log payment"}</button>
  {/snippet}
</Drawer>

<style>
  .when {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 84px;
    gap: 6px;
  }

  .form {
    display: grid;
    gap: 16px;
  }

  .dir {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding: 3px;
    border-radius: var(--r-md);
    background: var(--wash);
  }

  .dir button {
    min-height: 38px;
    border: 0;
    border-radius: 8px;
    background: transparent;
    font-weight: 600;
    font-size: var(--t-sm);
    color: var(--ink-2);
    cursor: pointer;
  }

  .dir button.on {
    background: var(--surface);
    color: var(--ink);
    box-shadow: 0 1px 2px rgb(0 0 0 / 0.08);
  }

  .people {
    border: 0;
    padding: 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  legend {
    font-size: var(--t-xs);
    font-weight: 600;
    color: var(--ink-2);
    margin-bottom: 6px;
  }

  .person {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-height: 36px;
    padding: 0 12px 0 8px;
    border-radius: 999px;
    border: 1px solid var(--rule-strong);
    background: var(--surface);
    font-size: var(--t-sm);
    font-weight: 550;
    cursor: pointer;
  }

  .person input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  .person.on {
    background: var(--sel);
    border-color: transparent;
  }

  .person:has(input:focus-visible) {
    outline: 2px solid var(--focus);
    outline-offset: 2px;
  }

  .row2 {
    display: grid;
    grid-template-columns: 1fr 1.3fr;
    gap: 12px;
  }

  .quick {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: -8px;
  }

  .hint-line {
    font-size: var(--t-sm);
    color: var(--ink-2);
  }

  .link {
    border: 0;
    background: none;
    padding: 0;
    color: var(--focus);
    font-weight: 600;
    cursor: pointer;
    text-decoration: underline;
  }

  .spacer {
    margin-right: auto;
  }
</style>
